/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import type {
    GetStaticPaths,
    GetStaticPropsContext,
    InferGetStaticPropsType,
    NextPage,
    PreviewData,
} from "next";
import { ParsedUrlQuery } from "querystring";

import { StrapiBlogTopic } from "@/types/strapi";

import { spacingSets } from "@/tokens/configs/spacing_config";
import { Spacing } from "@/tokens/spacing";

import { Button } from "@/ui/atoms/button";
import { GridColumn } from "@/ui/atoms/grid_column";
import { GridContainer } from "@/ui/atoms/grid_container";
import { Icon } from "@/ui/atoms/icon";
import { Link } from "@/ui/atoms/link";
import { SubgridContainer } from "@/ui/atoms/subgrid_container";
import { ArticleBody } from "@/ui/organisms/article_body";
import BlogArticleHero from "@/ui/organisms/blog_article_hero";
import { RelatedBlogArticleCardList } from "@/ui/organisms/blog_related_articles";

import { formatSocialIconSlug, generatePageLevelProps } from "@/util/data_util";
import { useTypedTheme } from "@/util/hooks/theme_hooks";
import { generateLegacyPreFooterData } from "@/util/strapi_data_util";
import {
    getBlogArticleSlugPage,
    getBlogArticleSlugs,
    getBlogGlobals,
    getGlobals,
    getRelatedBlogArticles,
} from "@/util/strapi_util";
import { buildStylesByBreakpoint } from "@/util/style_util";
import { getTrackEvent } from "@/util/tokens/track_event_util";

export const getStaticProps = async ({
    params,
    preview: previewMode,
}: GetStaticPropsContext<ParsedUrlQuery, PreviewData>) => {
    const slug = params?.slug as string | undefined;

    if (!slug) {
        return {
            notFound: true,
        };
    }

    const globalsData = await getGlobals(previewMode);
    const blogGlobalsData = await getBlogGlobals(previewMode);
    const pageData = await getBlogArticleSlugPage(slug, previewMode);

    const relatedArticles = await getRelatedBlogArticles(
        pageData.Topic as StrapiBlogTopic,
        slug,
        previewMode,
    );

    if (!pageData) {
        return {
            notFound: true,
        };
    }

    return {
        props: {
            ...generatePageLevelProps({
                path: `/blog/${slug}`,
                previewMode,
                slug,
            }),
            ...generateLegacyPreFooterData(globalsData),
            blogGlobalsData,
            globalsData,
            pageData,
            Related_Articles: relatedArticles,
        },
    };
};

export const getStaticPaths: GetStaticPaths = async () => {
    const slugs = await getBlogArticleSlugs();

    return {
        fallback: false,
        paths: slugs.map((slug) => ({ params: { slug } })),
    };
};

type PageProps = InferGetStaticPropsType<typeof getStaticProps>;

const Page: NextPage<PageProps> = (props) => {
    /**
     * Globals
     */
    const theme = useTypedTheme();

    /**
     * Styles
     */
    const ctaStyles = css({
        display: "block",
        marginLeft: "auto",
        marginRight: "auto",
        width: "fit-content",
    });

    const socialIconListStyles = css({
        alignItems: "center",
        display: "flex",
        gap: Spacing["spacing-3"],
        justifyContent: "center",
    });

    /**
     * Elements
     */
    const renderArticleEndCta = () => {
        const castedArticleCta = props.pageData.Article_CTA!;

        return castedArticleCta.map((CTA, _index) => {
            const _key = `cta-group::${CTA.id}::${_index}`;

            const track = getTrackEvent("BLOG_DOWNLOAD_WARP", {
                download_package: "dmg",
                os_category: "MacOS",
            });

            switch (CTA.__component) {
                case "primitives.primitive-link": {
                    return (
                        <Link
                            className={ctaStyles}
                            href={CTA.URL}
                            key={_key}
                            noWrap={true}
                            track={track}
                        >
                            {CTA.Text}
                        </Link>
                    );
                }
                default: {
                    return (
                        <Button
                            className={ctaStyles}
                            href={CTA.URL}
                            key={_key}
                            track={track}
                            variant="primary"
                        >
                            {CTA.Text}
                        </Button>
                    );
                }
            }
        });
    };

    const renderSocialLinks = () => {
        return (
            <GridColumn className={socialIconListStyles}>
                {props.blogGlobalsData.Social_Links.map(({ Text, URL }) => {
                    return (
                        <Link
                            ariaLabel={Text}
                            href={URL}
                            key={`blog-social-link::${Text}`}
                        >
                            <Icon
                                color={theme.ui.uiInactive}
                                slug={formatSocialIconSlug(Text)}
                                weight="fill"
                            />
                        </Link>
                    );
                })}
            </GridColumn>
        );
    };

    return (
        <div
            css={css(
                {
                    display: "flex",
                    flexDirection: "column",
                },
                buildStylesByBreakpoint(
                    "rowGap",
                    spacingSets.DefaultArticleMargin,
                ),
            )}
        >
            <BlogArticleHero {...props.pageData} />

            <GridContainer>
                <SubgridContainer
                    columnSpan={{
                        extraSmall: "full",
                        medium: 8,
                        large: 6,
                    }}
                    columnStart={{ extraSmall: 1, medium: 3, large: 4 }}
                >
                    <div css={css({ gridColumn: "1/-1" })}>
                        <ArticleBody
                            {...props.pageData}
                            shouldVideoLoop={props.slug === "world-of-warp"}
                            shouldVideoShowControls={
                                props.slug !== "world-of-warp"
                            }
                        />
                    </div>
                </SubgridContainer>
            </GridContainer>

            {props.pageData.Article_CTA && renderArticleEndCta()}

            {renderSocialLinks()}

            {props.Related_Articles.length > 0 && (
                <RelatedBlogArticleCardList
                    Related_Articles={props.Related_Articles}
                />
            )}
        </div>
    );
};

export default Page;
